import { collection, deleteDoc, doc, getDocs, query, setDoc, where, type Firestore } from 'firebase/firestore';
import { opportunityTimingStage, type CaseStudy, type Company, type Contact, type DashboardSummary, type EmailTemplate, type Mission, type Opportunity, type OutreachSequence } from '@client-outreach/shared';
import type { AppDataSnapshot, AppRepository } from './repository';

function withoutUndefined<T>(value:T):T {
  if(Array.isArray(value)) return value.filter(item=>item!==undefined).map(item=>withoutUndefined(item)) as T;
  if(value && typeof value==='object' && Object.getPrototypeOf(value)===Object.prototype){
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([,item])=>item!==undefined)
        .map(([key,item])=>[key,withoutUndefined(item)])
    ) as T;
  }
  return value;
}

export class FirebaseRepository implements AppRepository {
  mode = 'firestore' as const;
  constructor(private readonly db: Firestore, public readonly ownerUid: string) {}

  async loadAll(): Promise<AppDataSnapshot> {
    const [companies,contacts,opportunities,sequences,templates,caseStudies,missions] = await Promise.all([
      this.read<Company>('companies'), this.read<Contact>('contacts'), this.read<Opportunity>('opportunities'),
      this.read<OutreachSequence>('sequences'), this.read<EmailTemplate>('emailTemplates'), this.read<CaseStudy>('caseStudies'), this.read<Mission>('missions')
    ]);
    return {
      companies:companies.filter(x=>x.archived!==true),
      contacts:contacts.filter(x=>x.archived!==true),
      opportunities,
      missions,
      sequences:sequences.filter(x=>x.archived!==true),
      templates,
      caseStudies
    };
  }

  async dashboard(snapshot?:AppDataSnapshot):Promise<DashboardSummary> {
    const data = snapshot ?? await this.loadAll();
    const sequences=data.sequences.filter(x=>!x.archived);
    return {
      readyToContact:sequences.filter(x=>x.stage==='queue').length,
      scheduled:sequences.filter(x=>x.stage==='scheduled').length,
      awaitingResponse:sequences.filter(x=>x.stage==='sent'&&x.outcome==='awaiting-response').length,
      openingsSoon:data.opportunities.filter(x=>opportunityTimingStage(x)==='opening-soon').length,
      followUpsDue:sequences.filter(x=>x.followUpDueAt&&!x.followUpSentAt&&Date.parse(x.followUpDueAt)<=Date.now()).length,
    };
  }

  async upsertCompany(value:Company){await this.write('companies',value);return value;}
  async upsertContact(value:Contact){ await this.write('contacts',value); return value; }
  async upsertOpportunity(value:Opportunity){await this.write('opportunities',value);return value;}
  async upsertMission(value:Mission){await this.write('missions',value);return value;}
  async deleteMission(id:string){
    const ref=doc(this.db,'missions',id);
    // Firestore rules enforce owner-only deletion; contacts and outreach remain untouched.
    await deleteDoc(ref);
  }
  async upsertSequence(value:OutreachSequence){ await this.write('sequences',value); return value; }
  async deleteSequence(id:string){await deleteDoc(doc(this.db,'sequences',id));}
  async upsertTemplate(value:EmailTemplate){ await this.write('emailTemplates',value); return value; }
  async deleteTemplate(id:string){await deleteDoc(doc(this.db,'emailTemplates',id));}
  async upsertCaseStudy(value:CaseStudy){ await this.write('caseStudies',value); return value; }
  async deleteCaseStudy(id:string){await deleteDoc(doc(this.db,'caseStudies',id));}

  private async read<T>(name:string):Promise<T[]> {
    const snap = await getDocs(query(collection(this.db,name),where('ownerUid','==',this.ownerUid)));
    return snap.docs.map(x=>x.data() as T);
  }
  private async write<T extends {id:string;ownerUid:string}>(name:string,value:T):Promise<void> {
    if(value.ownerUid!==this.ownerUid) throw new Error('Owner mismatch');
    await setDoc(doc(this.db,name,value.id),withoutUndefined(value),{merge:true});
  }
}
